import { apiFetch } from './api';

export const BRANDS_MART_UNDERWRITER_CUTOFF = '2026-01-01';

export type BrandSmartWarrantyStatus = 'active' | 'expired' | 'none';
export type BrandSmartUnderwriter = 'Retailer A' | 'Retailer B';

export interface BrandSmartLookupRequest {
  email?: string;
  name?: string;
  phone?: string;
}

export interface BrandSmartWarrantyPreview {
  manufacturer: Exclude<BrandSmartWarrantyStatus, 'none'>;
  manufacturerExpiry: string;
  extended: BrandSmartWarrantyStatus;
  extendedExpiry: string | null;
  underwriter: BrandSmartUnderwriter | null;
}

export interface BrandSmartLookupPurchase {
  id: string;
  receiptNumber: string;
  productName: string;
  brand: string;
  model: string;
  category: string;
  subCategory?: string | null;
  purchaseDate: string;
  purchasePrice?: number;
  productImageUrl?: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  warrantyPreview: BrandSmartWarrantyPreview;
}

export interface BrandSmartLookupResponseData {
  matched: boolean;
  purchases: BrandSmartLookupPurchase[];
  matchedOn: ('email' | 'phone')[];
  emailChecked: boolean;
  phoneChecked: boolean;
}

export interface BrandSmartImportItem {
  purchaseId: string;
  targetHomeId: string | null;
  createNewHome: boolean;
}

export interface BrandSmartImportRequest {
  imports: BrandSmartImportItem[];
}

export interface BrandSmartImportResponseData {
  importedCount: number;
  assets: Array<{ id: string; name: string }>;
  newHomesCreated: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export async function brandsmartLookup(
  payload: BrandSmartLookupRequest,
): Promise<BrandSmartLookupResponseData> {
  const res = await apiFetch<ApiResponse<BrandSmartLookupResponseData>>('/v1/brandsmart/lookup', {
    method: 'POST',
    body: payload,
  });

  return res.data;
}

export async function brandsmartImport(
  payload: BrandSmartImportRequest,
): Promise<BrandSmartImportResponseData> {
  const res = await apiFetch<ApiResponse<BrandSmartImportResponseData>>('/v1/brandsmart/import', {
    method: 'POST',
    body: payload,
  });

  return res.data;
}

// ─── OTP flow (unified — matches mobile app) ──────────────────────────────────

export interface SendOtpRequest {
  identifier: string; // email or phone
}

export interface VerifyOtpRequest {
  identifier: string;
  otp: string;
}

export interface VerifyOtpResponseData {
  matched: boolean;
  purchases: BrandSmartLookupPurchase[];
}

export async function brandsmartSendOtp(
  payload: SendOtpRequest,
): Promise<void> {
  await apiFetch<ApiResponse<unknown>>('/v1/brandsmart/send-otp', {
    method: 'POST',
    body: payload,
  });
}

export async function brandsmartVerifyOtp(
  payload: VerifyOtpRequest,
): Promise<VerifyOtpResponseData> {
  const res = await apiFetch<ApiResponse<VerifyOtpResponseData>>(
    '/v1/brandsmart/verify-otp',
    { method: 'POST', body: payload },
  );
  return res.data;
}

// ─── AUTO-LOOKUP ─────────────────────────────────────────────────────────────────────────

/**
 * Auto-lookup: finds un-imported purchases linked to the currently authenticated
 * user's own email / phone (no parameters required).
 *
 * Mirrors the Flutter app's BrandsmartApiService.autoLookup().
 * Use this on login / home screen to automatically surface un-imported purchases.
 */
export async function brandsmartAutoLookup(): Promise<BrandSmartLookupResponseData> {
  const res = await apiFetch<ApiResponse<BrandSmartLookupResponseData>>('/v1/brandsmart/auto-lookup', {
    method: 'POST',
    body: {},
  });
  return res.data;
}

// ─── ASSET IMAGE POLLING ─────────────────────────────────────────────────────────────

export interface AssetImageEntry {
  assetId: string;
  productImageUrl: string | null;
}

export interface AssetImagesResponseData {
  images: AssetImageEntry[];
}

/**
 * Polls the backend for the current productImageUrl of the given asset IDs.
 * Returns a map of assetId → productImageUrl (null if not yet enriched by SERP).
 *
 * Mirrors the Flutter app's BrandsmartApiService.fetchAssetImages().
 * Use after import to progressively populate product images once SERP resolves.
 */
export async function brandsmartFetchAssetImages(
  assetIds: string[],
): Promise<Record<string, string | null>> {
  if (assetIds.length === 0) return {};
  const res = await apiFetch<ApiResponse<AssetImagesResponseData>>(
    `/v1/brandsmart/asset-images?ids=${assetIds.join(',')}`,
  );
  return Object.fromEntries(
    res.data.images.map((item) => [item.assetId, item.productImageUrl]),
  );
}
